import * as CL from '@design-system/component-library';

import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { requiresNetworkConstructionMsg, t } from '../../../../common/i18n/index.js';
import { OFFieldApi } from '../../../../generated/api/oFFieldApi.js';
import { type OpenFormContext } from '../../OpenFormAnswers.js';
import { OpenFormChargeInput } from '../../OpenFormComponents/OpenFormChargeInput.js';
import { OpenFormCheckbox } from '../../OpenFormComponents/OpenFormCheckbox.js';
import { OpenFormGridCol, OpenFormGridRow } from '../../OpenFormComponents/OpenFormGrid.js';
import { OpenFormKnowledgeArticleLink } from '../../OpenFormComponents/OpenFormKnowledgeArticleLink.js';
import { OpenFormTag } from '../../OpenFormComponents/OpenFormTag.js';
import { setValueOptions } from '../../OpenFormHooks/useOpenFormMethods.js';
import { Table } from '../../OpenFormHooks/useTable.js';
import { concat, merge, onBlurPrice, onChangePrice } from '../../OpenFormUtils.js';
import type { OpenFormChoice } from '../../../../generated/api/openFormChoice.js';
import type { OpenFormListColumn } from '../../../../generated/api/openFormListColumn.js';

export const OpenFormQuestionListOfObjects = ({
  disabled,
  required,
  label,
  name,
  multiselect,
  columns,
  visible,
  context = {},
  availability,
  infoText,
}: {
  disabled: boolean;
  required: boolean;
  label: string;
  name: string;
  multiselect: boolean;
  columns: OpenFormListColumn[];
  visible: OpenFormChoice[];
  context: OpenFormContext | undefined;
  availability: Record<string, boolean>;
  infoText?: string;
}) => {
  const { setValue } = useFormContext();
  const charges = useMemo(() => context.charges ?? {}, [context.charges]);
  const choices = useMemo(() => new Set(context.choices), [context.choices]);

  // If there are both `description__c` and `name` columns, hide `description__c` column
  const cols = useMemo(() => {
    const list = columns.map((column, index) => ({
      align: !index ? ('left' as const) : ('right' as const),
      key: String(index),
      label: column.label ?? null,
      visible: column.name === OFFieldApi.description__c ? !columns.some(c => c.name === 'name') : true,
    }));
    list.splice(1, 0, { align: 'right', key: 'tag', label: null, visible: true });
    list.splice(0, 0, { align: 'left', key: 'checkbox', label: null, visible: true });
    return list;
  }, [columns]);

  const rows = useMemo(() => {
    return visible
      .map(({ guid, values }) => ({ available: availability[guid], choice: guid, values }))
      .sort(Table.compare('available'));
  }, [visible, availability]);

  const setCharge = useCallback(
    (choice: string, field: string, value: string) => {
      charges[choice][field] = value;
      setValue(name + '.charges', charges);
    },
    [name, setValue, charges]
  );

  const rowClick = useCallback(
    (row: { choice: string; values: string[] }) => {
      if (!disabled) {
        if (choices.has(row.choice)) {
          choices.delete(row.choice) && delete charges[row.choice];
        } else {
          !multiselect && choices.forEach(id => choices.delete(id) && delete charges[id]);
          const choiceObject = visible.find(choice => choice.guid === row.choice);

          charges[row.choice] = Object.assign(
            merge((column, index) => ({ [column.name]: row.values[index] }), ...columns),
            choiceObject?.linkedValue ? { linkedValue: choiceObject.linkedValue } : {},
            choices.add(row.choice) && charges[row.choice]
          );
        }
        setValue(name + '.charges', charges);
        setValue(name, Array.from(choices), setValueOptions);
      }
    },
    [disabled, name, multiselect, columns, setValue, charges, choices, visible]
  );

  const rowFields = useCallback(
    (row: { choice: string; values: string[] }) => {
      const description = row.values[columns.findIndex(c => c.name === OFFieldApi.description__c)];
      return merge(
        (field, index) => ({ [index]: field }),
        ...columns.map((column, index) => {
          switch (column.name) {
            case OFFieldApi.one_time_charge__c:
            case OFFieldApi.recurring_charge__c:
              return (
                <OpenFormChargeInput
                  className={classNames({
                    ['of-openform__view__list-of-objects-table__cell-disabled']: disabled,
                    ['of-openform__view__list-of-objects-table__cell-editable']: true,
                    ['of-openform__view__list-of-objects-table__cell-readonly']: column.isReadOnly,
                  })}
                  disabled={disabled}
                  required={required}
                  readOnly={column.isReadOnly}
                  prefix={column.prefix}
                  suffix={column.suffix}
                  value={onChangePrice(charges[row.choice]?.[column.name] || row.values[index])}
                  onBlur={e =>
                    choices.has(row.choice) &&
                    setCharge(row.choice, column.name, onBlurPrice(e.target.value || row.values[index]))
                  }
                  onChange={e => {
                    choices.has(row.choice) || rowClick(row);
                    setCharge(row.choice, column.name, onChangePrice(e.target.value || row.values[index]));
                  }}
                  onClick={e => choices.has(row.choice) && e.stopPropagation()}
                />
              );
            case OFFieldApi.support_knowledgebase_url__c: {
              const knowledgeBaseURL = row.values[index];
              const isValidURL = knowledgeBaseURL && /^https?:\/\//i.test(knowledgeBaseURL);
              return (
                <div
                  className={classNames({
                    ['of-openform__view__list-of-objects-table__cell-disabled']: disabled,
                    ['of-openform__view__list-of-objects-table__cell-readonly']: true,
                  })}
                >
                  {isValidURL && <OpenFormKnowledgeArticleLink knowledgeBaseURL={knowledgeBaseURL} />}
                </div>
              );
            }
            default:
              return (
                <div
                  className={classNames({
                    ['of-openform__view__list-of-objects-table__cell-disabled']: disabled,
                    ['of-openform__view__list-of-objects-table__cell-readonly']: true,
                  })}
                >
                  <div>{concat(column.prefix, row.values[index], column.suffix).join(' ')}</div>
                  {column.name === 'name' && description ? <div>{description}</div> : null}
                </div>
              );
          }
        })
      );
    },
    [disabled, required, columns, charges, choices, setCharge, rowClick]
  );

  return !rows.length ? null : (
    <OpenFormGridRow>
      <OpenFormGridCol colWidth={9} className={classNames({ ['label--mandatory']: required })}>
        <div className="of-openform__view__list-of-objects-labelarea">
          <label>{label}</label>
          {infoText && (
            <CL.Popover triggerElement={<CL.Icon icon="information" size="m" color="blue-600" />} placement="top">
              {infoText}
            </CL.Popover>
          )}
        </div>
        <CL.Table
          className="of-openform__view__list-of-objects-table"
          columns={cols}
          hover={true}
          rowClicks={rows.map(row => () => rowClick(row))}
          rows={rows.map(row => {
            const active = choices.has(row.choice);
            return Object.assign(rowFields(row), {
              active: active,
              checkbox: <OpenFormCheckbox checked={active} disabled={disabled} value={row.choice} />,
              tag: row.available ? null : <OpenFormTag type="warning" value={t.OTPK(requiresNetworkConstructionMsg)} />,
            });
          })}
          tableType="bordered"
          valignText="middle"
        />
      </OpenFormGridCol>
    </OpenFormGridRow>
  );
};
